import { logger } from '@adornis/base/logging.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { getLLMRouter } from '../llm/llm-gate.js';
import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import { readFile } from 'node:fs/promises';
import { extractImageFromPDF } from '../../../db/utils/transform-file.js';
import { FLASH_PAGE_LIMIT } from '../../../shared/constants.js';
import {
  longSplitAgentModel,
  longSplitAgentTemperature,
  shortSplitAgentModel,
  shortSplitAgentTemperature,
} from '../../../shared/models.js';
import { ConfirmationProgressStatus, setSplitConfirmationProgressByExam } from '../../confirmation-progress.js';
import { splitPdfInWorker } from '../../utils/run-in-worker.js';
import { imagesToBase64URLsWithSizeFallback } from '../../utils/transform-file.js';
import { addCostToExam } from '../add-cost.js';
import { resolveIsStudentOwnedExam } from '../../utils/exam-role.js';
import type { EvaluatedStudentBlock } from './calculate-split-confidence.js';
import { calculateSplitConfidenceStats, evaluateSplitConfidence } from './calculate-split-confidence.js';

function fmtMem(): string {
  const m = process.memoryUsage();
  return `rss=${(m.rss / 1024 / 1024).toFixed(0)}MB heap=${(m.heapUsed / 1024 / 1024).toFixed(0)}MB ext=${(m.external / 1024 / 1024).toFixed(0)}MB`;
}

export function getStudentNamesFromBlocks(studentBlocks: Array<{ student_name: string | null }>): string[] {
  return studentBlocks.map((block, idx) =>
    typeof block.student_name === 'string' && block.student_name.length > 0 ? block.student_name : `Schüler ${idx + 1}`,
  );
}

const SYSTEM_PROMPT = `Your task is to group document pages by student submission and extract the student name for each block.

STRICT RULES:
1. Pages belonging to a student MUST be consecutive.
2. A student submission can only appear once.
3. All pages must be assigned to exactly one student.
4. Students typically have the same number of pages, but slight variations are allowed for extra writing paper.
5. Each student MUST have the same set of tasks!. It is impossible that one student has a different set of tasks than another student.
6. No student can have the same task twice because duplicates indicate that the ordering is off and pages from different students are mixed.
6. Pay close attention to the FIRST page of every block. It should visually look like the start of an exam (e.g., Title page, "Name:" field, or Task 1).
7. Do NOT use writing styles to split students.
8. Prefer the MINIMUM number of students that satisfies all rules.

If a grouping violates any rule, it is invalid and must not be returned.
Process:
STEP 1 (page_analysis): Analyze every single page sequentially. Identify the primary task number or the printed page number
STEP 2 (block_determination): Determine possible student blocks using ONLY consecutive pages.
STEP 3: Verify NO block contains duplicate tasks. If it does, split the block.
STEP 4: Verify each block has exactly the same set of tasks.
STEP 5: For each block, verify if the first page clearly contains a start marker.
STEP 6: Verify that each block contains the same set of tasks.
STEP 7: For each block, extract the student name ONLY if you are certain.
STEP 8: Choose the valid solution with the smallest number of students.
STEP 9: Assign confidence scores based on visual clarity and readability.
STEP 9: Verify that all rules are satisfied. If any rule is violated, assign a confidence of 0.1 to the affected blocks and add a descriptive issue message.

**Return STRICT JSON only:**

{
  "total_students": 2,
  "student_blocks": [
    {
      "student_id": 1,
      "pages": [1, 2, 3],
      "student_name": "Max Mustermann",
      "has_start_marker": true,
      "name_extraction_confidence": 0.95,
      "block_confidence": 1.0
    },
    {
      "student_id": 2,
      "pages": [4, 5],
      "student_name": null,
      "has_start_marker": false,
      "name_extraction_confidence": 0.0,
      "block_confidence": 0.8
    }
  ]
}
`;

@Entity()
class StudentBlockDTO extends AdornisEntity {
  static override _class = 'StudentBlockDTO';

  @Field(() => String)
  block_uid!: string;

  @Field(() => String)
  _id!: string;

  @Field(() => Int)
  student_id!: number;

  @Field(() => [Int])
  pages!: number[];

  @Field(() => String)
  student_name!: string | null;

  @Field(() => Boolean)
  has_start_marker!: boolean;

  @Field(() => Float)
  name_extraction_confidence!: number;

  @Field(() => Float)
  block_confidence!: number;

  @Field(() => Float)
  final_confidence!: number;

  @Field(() => [String])
  block_issues!: string[];

  @Field(() => [String])
  block_issue_codes!: string[];
}

@Entity()
class SplitAgentDataDTO extends AdornisEntity {
  static override _class = 'SplitAgentDataDTO';

  @Field(() => [String])
  dataUrls!: string[];

  @Field(() => [StudentBlockDTO])
  studentBlocks!: StudentBlockDTO[];

  @Field(() => Int)
  totalPages!: number;
}

export interface SplitAgentRunResult {
  files: File[];
  issues: string[];
  issueDetails?: SplitIssueDetail[];
  studentBlocks?: EvaluatedStudentBlock[];
  studentNames?: string[];
  rawBlocks?: any[];
}

export interface SplitIssueDetail {
  code:
    | 'pages_not_assigned_once'
    | 'non_consecutive_pages'
    | 'split_agent_failure'
    | 'missing_start_boundary'
    | 'transition_uncertain'
    | 'missing_student_name'
    | 'name_uncertain'
    | 'length_outlier'
    | 'outlier_long'
    | 'outlier_short'
    | 'outlier_long_extreme'
    | 'outlier_short_extreme';
  severity: 'warning' | 'error';
  blockUid?: string;
  studentId?: number;
  studentName?: string | null;
  sourceFileName?: string;
}

interface SplitStudentBlockInput {
  block_uid: string;
  student_id: number;
  pages: number[];
  student_name: string | null;
  has_start_marker: boolean;
  name_extraction_confidence: number;
  block_confidence: number;
}

type NormalizedRawStudentBlock = EvaluatedStudentBlock & {
  has_start_marker?: boolean;
  name_extraction_confidence?: number;
  block_confidence?: number;
};

function normalizeStudentBlock(rawBlock: any, fallbackStudentId: number): NormalizedRawStudentBlock {
  const source = rawBlock?.doc ?? rawBlock ?? {};

  const pages = Array.isArray(source.pages)
    ? source.pages.map((page: any) => Number(page)).filter((page: number) => Number.isFinite(page))
    : [];

  const rawConfidence = Number(source.final_confidence);
  const confidence = Number.isFinite(rawConfidence) ? rawConfidence : 0;
  const rawNameExtractionConfidence = Number(source.name_extraction_confidence);
  const nameExtractionConfidence = Number.isFinite(rawNameExtractionConfidence)
    ? rawNameExtractionConfidence
    : undefined;
  const rawBlockConfidence = Number(source.block_confidence);
  const blockConfidence = Number.isFinite(rawBlockConfidence)
    ? rawBlockConfidence
    : Number.isFinite(rawConfidence)
      ? rawConfidence
      : undefined;

  return {
    block_uid:
      typeof source.block_uid === 'string' && source.block_uid.trim().length > 0 ? source.block_uid : undefined,
    student_id: Number(source.student_id) || fallbackStudentId,
    pages,
    student_name:
      typeof source.student_name === 'string' && source.student_name.trim().length > 0 ? source.student_name : null,
    has_start_marker: typeof source.has_start_marker === 'boolean' ? source.has_start_marker : undefined,
    name_extraction_confidence: nameExtractionConfidence,
    block_confidence: blockConfidence,
    final_confidence: confidence,
    block_issues: Array.isArray(source.block_issues) ? source.block_issues.map((issue: any) => String(issue)) : [],
    block_issue_codes: Array.isArray(source.block_issue_codes)
      ? source.block_issue_codes.map((issueCode: any) => String(issueCode))
      : [],
  };
}

export const STUDENT_GROUPING_JSON_SCHEMA = {
  name: 'student_page_grouping',
  strict: true,
  schema: {
    type: 'object',
    additionalProperties: false,
    required: ['total_students', 'student_blocks'],
    properties: {
      total_students: { type: 'integer', minimum: 1 },
      student_blocks: {
        type: 'array',
        minItems: 1,
        items: {
          type: 'object',
          additionalProperties: false,
          required: [
            'student_id',
            'pages',
            'student_name',
            'has_start_marker',
            'name_extraction_confidence',
            'block_confidence',
          ],
          properties: {
            student_id: { type: 'integer', minimum: 1 },
            pages: {
              type: 'array',
              minItems: 1,
              items: { type: 'integer', minimum: 1 },
            },
            student_name: {
              anyOf: [{ type: 'string', minLength: 1 }, { type: 'null' }],
              description: 'Student name if confidently extracted, otherwise null.',
            },
            has_start_marker: {
              type: 'boolean',
              description:
                'True if the first page of this block clearly shows the start of an exam (e.g., a header, title, "Name:" field, or Task 1).',
            },
            name_extraction_confidence: {
              type: 'number',
              description:
                '0.0 to 1.0. How sure are you that the extracted name is actually a student name? Use 0.0 if student_name is null.',
            },
            block_confidence: {
              type: 'number',
              description:
                '0.0 to 1.0. How confident are you that these specific pages represent exactly one complete student submission based on visual clarity?',
            },
          },
        },
      },
    },
  },
};

function dataUrlToFile(dataUrl: string, fileName: string): File {
  const [metaPart, base64Part] = dataUrl.split(',');
  const mimeTypeMatch = metaPart?.match(/data:(.*?);base64/);
  const mimeType = mimeTypeMatch?.[1] || 'application/pdf';
  const binary = atob(base64Part || '');
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new File([bytes], fileName, { type: mimeType });
}

async function markSplitAgentFailed(examId: string, error: unknown): Promise<void> {
  try {
    await setSplitConfirmationProgressByExam(examId, {
      status: ConfirmationProgressStatus.FAILED,
      confirmed: false,
      message: 'splitAgentFailed',
    });
  } catch (progressErr) {
    logger.error({ err: progressErr, context: 'Split' }, 'Konnte splitConfirmationState nicht auf FAILED setzen');
  }
}

async function groupPdfPagesByStudent(
  pdfId: string,
  numPages: number,
  examId: string,
): Promise<SplitStudentBlockInput[]> {
  logger.info({ pdfId, numPages, mem: fmtMem(), context: 'split-agent:start' }, 'Starting split agent');

  logger.info({ pdfId, mem: fmtMem(), context: 'split-agent:phase1:start' }, 'Extracting images from PDF');
  const imageIds = await extractImageFromPDF(pdfId);
  logger.info(
    { imageCount: imageIds.length, mem: fmtMem(), context: 'split-agent:phase1:done' },
    'Extracted page images',
  );

  const fileCollection = await getCollection(AdornisFile);
  const files: AdornisFile[] = [];
  for (const imageId of imageIds) {
    const file = await fileCollection.findOne({ _id: imageId });
    if (file) files.push(file as AdornisFile);
  }

  logger.info(
    { imageCount: files.length, mem: fmtMem(), context: 'split-agent:phase2:start' },
    'Encoding images for LLM',
  );
  const pageImageDataUrls = await imagesToBase64URLsWithSizeFallback(files, {
    ocrEnhance: false,
    logTag: '[Split]',
  });
  logger.info(
    { dataUrlCount: pageImageDataUrls.length, mem: fmtMem(), context: 'split-agent:phase2:done' },
    'Encoded data URLs',
  );

  if (pageImageDataUrls.length !== numPages) {
    throw new Error(`Split agent preparation failed: expected ${numPages} pages but got ${pageImageDataUrls.length}`);
  }

  // Spot-check: do data URLs all share the same prefix (= same encoder path), or did some
  // silently fall back to a different format?
  const prefixCounts = new Map<string, number>();
  for (const url of pageImageDataUrls) {
    const head = url.slice(0, Math.min(40, url.includes(',') ? url.indexOf(',') : 40));
    prefixCounts.set(head, (prefixCounts.get(head) ?? 0) + 1);
  }

  const router = getLLMRouter();
  const messagesPayload: any = [
    { role: 'system', content: SYSTEM_PROMPT },
    {
      role: 'user',
      content: [
        {
          type: 'text',
          text: `Group this ${numPages}-page exam file into student submissions. Use the page images in order.`,
        },
        ...pageImageDataUrls.flatMap((url, index) => [
          { type: 'text', text: `Page ${index + 1}:` },
          { type: 'image_url', image_url: { url, detail: 'low' } },
        ]),
      ],
    },
  ];
  const selectedModel = numPages > FLASH_PAGE_LIMIT ? longSplitAgentModel : shortSplitAgentModel;
  const selectedTemperature = numPages > FLASH_PAGE_LIMIT ? longSplitAgentTemperature : shortSplitAgentTemperature;

  let response: any;
  try {
    response = await router.chat.completions.create({
      stream: false,
      model: selectedModel,
      provider: 'openrouter',
      temperature: selectedTemperature,
      max_tokens: 60000,
      response_format: { type: 'json_object' },
      messages: messagesPayload,
    });
  } catch (error) {
    logger.error({ err: error, context: 'split-agent' }, 'Critical LLM Router call failed');
    throw new Error(`Split agent calculation aborted due to upstream AI provider failure.`);
  }

  const finishReason = response?.choices?.[0]?.finish_reason;
  const usage = response?.usage;
  logger.info({ cost: JSON.stringify(usage?.cost), context: 'COST_TRACKING' }, 'SplitFile Agent cost');
  await addCostToExam({ usage }, examId, 'splitFile-initial');

  logger.info(
    {
      finishReason,
      totalTokens: usage?.total_tokens ?? 'unknown',
      promptTokens: usage?.prompt_tokens ?? '?',
      completionTokens: usage?.completion_tokens ?? '?',
      context: 'split-agent',
    },
    'LLM finished',
  );
  if (finishReason === 'length') {
    logger.warn(
      { context: 'split-agent' },
      'LLM output was truncated — max_tokens limit reached. Split result may be incomplete.',
    );
  }
  const rawContent: any = response.choices[0]?.message?.content;
  const content =
    typeof rawContent === 'string'
      ? rawContent
      : Array.isArray(rawContent)
        ? rawContent
            .map((item: any) => (typeof item?.text === 'string' ? item.text : typeof item === 'string' ? item : ''))
            .join('')
            .trim()
        : '';

  if (!content) {
    throw new Error('Split agent returned empty response');
  }
  const parsedRaw = JSON.parse(content);
  const parsed = Array.isArray(parsedRaw) ? parsedRaw[0] : parsedRaw;
  const rawBlocks = parsed?.student_blocks;

  if (!Array.isArray(rawBlocks) || rawBlocks.length === 0) {
    throw new Error('Split agent response has no student_blocks');
  }

  // Exam metadata (subject/topic/class) is NOT read here: the split can run after the master solution
  // and the grading scheme, so it is extracted in the structure step instead (see queueExamMetadataTask).
  return rawBlocks.map((block: any, index: number) => ({
    ...(block || {}),
    block_uid: `${pdfId}:block-${index + 1}`,
  }));
}

async function splitPdfByBlocks(pdfId: string, blocks: Array<{ pages: number[] }>): Promise<string[]> {
  const projection = { _id: 1, createdAt: 1, meta: { extension: 1, fileName: 1, mimetype: 1, size: 1 } } as const;
  const pdfFile = await getByID(PDFFile, pdfId)(projection);
  if (!pdfFile) throw new Error(`Cannot find PDF with ID ${pdfId}`);

  const sourceBytes = await readFile(pdfFile.getFullPath());
  return splitPdfInWorker(sourceBytes, blocks);
}

const runSplitFileAgentDataMutation = registerMutation({
  operationName: 'runSplitFileAgentMutation',
  type: () => SplitAgentDataDTO,
  params: [
    { name: 'pdfId', type: () => String },
    { name: 'examId', type: () => String },
  ],
  resolve: (pdfId: string, examId: string) => async (): Promise<SplitAgentDataDTO> => {
    try {
      const projection = { _id: 1, createdAt: 1, meta: { extension: 1, fileName: 1, mimetype: 1, size: 1 } } as const;
      const pdfFile = await getByID(PDFFile, pdfId)(projection);
      if (!pdfFile) {
        return new SplitAgentDataDTO({ dataUrls: [], studentBlocks: [], totalPages: 0 });
      }

      const numPages = await pdfFile.getNumPages();
      // A student only ever uploads their own single exam — never a multi-student class PDF — so
      // the split agent's per-student grouping is skipped for them regardless of page count, the
      // same way it already is for a genuinely single-page upload below.
      const isStudentSelfUpload = await resolveIsStudentOwnedExam(examId);
      if (numPages <= 1 || isStudentSelfUpload) {
        const fullPath = pdfFile.getFullPath();
        const dataUrl = `data:application/pdf;base64,${await readFile(fullPath, { encoding: 'base64' })}`;
        return new SplitAgentDataDTO({
          dataUrls: [dataUrl],
          studentBlocks: [
            new StudentBlockDTO({
              block_uid: `${pdfId}:block-1`,
              _id: `block-1`,
              student_id: 1,
              pages: Array.from({ length: numPages }, (_, i) => i + 1),
              student_name: null,
              has_start_marker: true,
              name_extraction_confidence: 0,
              block_confidence: 1,
              final_confidence: 1.0,
              block_issues: [],
              block_issue_codes: [],
            }),
          ],
          totalPages: numPages,
        });
      }

      const rawBlocksWithUid = await groupPdfPagesByStudent(pdfId, numPages, examId);
      const dataUrls = await splitPdfByBlocks(pdfId, rawBlocksWithUid);

      return new SplitAgentDataDTO({
        dataUrls,
        studentBlocks: rawBlocksWithUid.map(
          (block, index) =>
            new StudentBlockDTO({
              _id: block.block_uid || `block-${block.student_id}`,
              block_uid: block.block_uid,
              student_id: Number(block.student_id) || index + 1,
              pages: Array.isArray(block.pages) ? block.pages : [],
              student_name: typeof block.student_name === 'string' ? block.student_name : null,
              has_start_marker: block.has_start_marker,
              name_extraction_confidence:
                typeof block.name_extraction_confidence === 'number' ? block.name_extraction_confidence : 0,
              block_confidence: typeof block.block_confidence === 'number' ? block.block_confidence : 1,
              final_confidence: typeof block.block_confidence === 'number' ? block.block_confidence : 1,
              block_issues: [],
              block_issue_codes: [],
            }),
        ),
        totalPages: numPages,
      });
    } catch (error) {
      logger.error({ err: error, context: 'runSplitFileAgentMutation' }, 'Mutation execution failed');
      await markSplitAgentFailed(examId, error);
      throw error;
    }
  },
});

export async function runSplitFileAgentMutation(files: File[], examId: string): Promise<SplitAgentRunResult> {
  const splitRuns = await Promise.all(
    files.map(async (file): Promise<SplitAgentRunResult> => {
      if (file.type !== 'application/pdf') {
        return { files: [file], issues: [], issueDetails: [], studentBlocks: [] };
      }

      const sourcePdfId = typeof (file as any)?.sourcePdfId === 'string' ? (file as any).sourcePdfId : undefined;
      const originalPdfId = sourcePdfId || (await PDFFile.upload(file));

      try {
        const result = await (runSplitFileAgentDataMutation(originalPdfId, examId) as any)({
          dataUrls: 1,
          totalPages: 1,
          studentBlocks: {
            block_uid: 1,
            _id: 1,
            student_id: 1,
            pages: 1,
            student_name: 1,
            has_start_marker: 1,
            name_extraction_confidence: 1,
            block_confidence: 1,
            final_confidence: 1,
            block_issues: 1,
            block_issue_codes: 1,
          },
        });

        const splitDataUrls = result.dataUrls;
        const studentBlocksRaw = Array.isArray(result.studentBlocks) ? result.studentBlocks : [];
        const rawBlocks = studentBlocksRaw
          .map((block: any, index: number) => normalizeStudentBlock(block, index + 1))
          .map((block, index) => ({
            ...block,
            block_uid: block.block_uid || `${originalPdfId}:block-${index + 1}`,
          }));

        if (splitDataUrls.length === 0) {
          return { files: [file], issues: [], issueDetails: [], studentBlocks: [], rawBlocks: [] as any[] };
        }

        const splitFiles = splitDataUrls.map((dataUrl, index) => {
          const splitFile = dataUrlToFile(dataUrl, `submission-${index + 1}.pdf`);
          (splitFile as any).splitPdfDataUrl = dataUrl;
          return splitFile;
        });

        return {
          files: splitFiles,
          issues: [],
          issueDetails: [],
          studentBlocks: [],
          rawBlocks,
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        logger.error({ err: error, fileName: file.name, message }, 'Split agent failed for PDF');
        return {
          files: [],
          issues: [],
          issueDetails: [
            {
              code: 'split_agent_failure',
              severity: 'error',
              sourceFileName: file.name,
            },
          ],
          studentBlocks: [],
          rawBlocks: [],
        };
      }
    }),
  );

  const allRawBlocks = splitRuns.flatMap(run => (run as any).rawBlocks || []);

  const aggregated: SplitAgentRunResult = splitRuns.reduce<SplitAgentRunResult>(
    (acc, run) => {
      acc.files.push(...run.files);
      acc.issues.push(...run.issues);
      if (run.issueDetails) {
        acc.issueDetails = acc.issueDetails || [];
        acc.issueDetails.push(...run.issueDetails);
      }
      if (run.studentBlocks) {
        acc.studentBlocks = acc.studentBlocks || [];
        acc.studentBlocks.push(...run.studentBlocks);
      }
      return acc;
    },
    { files: [], issues: [], issueDetails: [], studentBlocks: [] },
  );

  if (allRawBlocks.length === 0) {
    logger.warn({ context: 'Split' }, 'Aborting post-processing math execution due to an empty raw blocks collection.');
    aggregated.studentNames = [];
    return aggregated;
  }

  try {
    const globalStats = calculateSplitConfidenceStats(allRawBlocks);
    const evaluatedBlocks = evaluateSplitConfidence(allRawBlocks, globalStats);

    aggregated.studentBlocks = evaluatedBlocks;
    aggregated.issueDetails = [];
    const nonConsecutiveStudentIds = new Set<string>();

    for (const block of evaluatedBlocks) {
      for (const code of block.block_issue_codes || []) {
        switch (String(code)) {
          case 'missing_start_boundary':
          case 'transition_uncertain':
          case 'missing_student_name':
          case 'length_outlier':
          case 'outlier_long':
          case 'outlier_short':
          case 'outlier_long_extreme':
          case 'outlier_short_extreme':
          case 'name_uncertain':
            aggregated.issueDetails.push({
              code: String(code) as SplitIssueDetail['code'],
              severity:
                code === 'missing_start_boundary' ||
                code === 'transition_uncertain' ||
                code === 'outlier_long_extreme' ||
                code === 'outlier_short_extreme'
                  ? 'error'
                  : 'warning',
              blockUid: block.block_uid,
              studentId: block.student_id,
              studentName: block.student_name,
            });
            break;
        }
      }
    }

    for (const block of evaluatedBlocks) {
      const blockUid = block.block_uid || `fallback:${block.student_id}`;
      if (
        !nonConsecutiveStudentIds.has(blockUid) &&
        Array.isArray(block.block_issue_codes) &&
        block.block_issue_codes.includes('non_consecutive_pages')
      ) {
        nonConsecutiveStudentIds.add(blockUid);
        aggregated.issueDetails.push({
          code: 'non_consecutive_pages',
          severity: 'error',
          blockUid: block.block_uid,
          studentId: block.student_id,
          studentName: block.student_name,
        });
      }
    }

    aggregated.studentNames = getStudentNamesFromBlocks(aggregated.studentBlocks || []);
  } catch (evalError) {
    logger.error(
      { err: evalError, context: 'Split' },
      'Encountered exception during post-split analytics calculations',
    );
    aggregated.issueDetails = [{ code: 'split_agent_failure', severity: 'error' }];
    aggregated.studentNames = [];
  }

  if (aggregated.issues.length > 0) {
    logger.warn({ issues: aggregated.issues.join(', ') }, 'Split agent reported issues');
  }
  return aggregated;
}
